
import React from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Check, Loader2 } from "lucide-react";

interface SubscriptionSheetViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubscribe: () => Promise<void>;
  isSubscribing: boolean;
  errorMessage: string | null;
  primaryColor: string;
}

export function SubscriptionSheetView({
  open,
  onOpenChange,
  onSubscribe,
  isSubscribing,
  errorMessage,
  primaryColor
}: SubscriptionSheetViewProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[100dvh] p-0 sm:max-w-none w-full">
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-auto p-6">
            <div className="flex flex-col items-center mb-6">
              <img 
                src="/lovable-uploads/63dc6fcc-849b-4d08-a3d0-a0fa7dfa124d.png" 
                alt="Grace Awakens Logo" 
                className="h-16 mb-4"
              />
              <h2 className="text-2xl font-semibold text-center mb-2">Church Plan Subscription</h2>
              <p className="text-center text-muted-foreground">You're about to subscribe to our Church Plan</p>
            </div>
            
            <div className="space-y-6">
              <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-2">Church Plan - $49.99/month</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>All Premium features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Analytics dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Custom branding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Bulk NFC management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Weekly sermon plans based on congregation interests</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Insights from prayer requests and journals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Priority support</span>
                  </li>
                </ul>
              </div>
              
              <p className="text-sm text-muted-foreground">
                You will be charged $49.99 monthly. You can cancel anytime from your account settings.
                Our team will contact you shortly to help set up your church profile and provide personalized support.
              </p>
              
              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-100 rounded text-red-600 text-sm">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6 border-t">
            <div className="grid grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                className="w-full"
              >
                Cancel
              </Button>
              <Button 
                onClick={onSubscribe}
                className="w-full"
                disabled={isSubscribing}
                style={{ backgroundColor: primaryColor }}
              >
                {isSubscribing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Subscribe Now"
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
